import {
  TableHeader as ShadcnTableHeader,
  TableHead as ShadcnTableHead,
  TableRow as ShadcnTableRow,
} from '@shadcn/ui/table';
import { flexRender, HeaderGroup, Header, ColumnDefTemplate, HeaderContext } from '@tanstack/react-table';
import { ArrowUp, ArrowDown } from 'lucide-react';
import React, { useCallback } from 'react';

import { TABLE_CONSTANTS } from '../constants';

interface ITableHeaderProps<TData> {
  readonly headerGroups: HeaderGroup<TData>[];
}

interface ITableHeaderCellProps<TData> {
  readonly canSort: boolean;
  readonly createKeyDownHandler: (
    header: Header<TData, unknown>,
    canSort: boolean
  ) => ((e: React.KeyboardEvent) => void) | undefined;
  readonly header: Header<TData, unknown>;
  readonly isFirstColumn: boolean;
  readonly isLastColumn: boolean;
  readonly name: ColumnDefTemplate<HeaderContext<TData, unknown>> | undefined;
  readonly sortDirection: false | 'asc' | 'desc';
}

function renderSortIcon(sortDirection: false | 'asc' | 'desc') {
  if (sortDirection === 'asc') {
    return <ArrowUp className="ml-2 h-4 w-4" />;
  } else if (sortDirection === 'desc') {
    return <ArrowDown className="ml-2 h-4 w-4" />;
  }
  return null;
}

function useTableSortHandlers<TData>() {
  const createKeyDownHandler = useCallback((header: Header<TData, unknown>, canSort: boolean) => {
    if (!canSort) return undefined;

    return (e: React.KeyboardEvent) => {
      if (canSort && (e.key === 'Enter' || e.key === ' ')) {
        header.column.getToggleSortingHandler()?.(e);
        e.preventDefault();
      }
    };
  }, []);

  return { createKeyDownHandler };
}

function TableHeaderCell<TData>({
  header,
  name,
  canSort,
  sortDirection,
  isFirstColumn,
  isLastColumn,
  createKeyDownHandler,
}: ITableHeaderCellProps<TData>) {
  const { STYLING } = TABLE_CONSTANTS;
  const handleKeyDown = createKeyDownHandler(header, canSort);

  let headerContent;

  if (header.isPlaceholder) {
    headerContent = null;
  } else if (canSort) {
    headerContent = (
      <button
        aria-label={`Sort by ${name}`}
        className="flex items-center cursor-pointer select-none appearance-none bg-transparent border-0 p-0 m-0 text-inherit font-inherit w-full text-left"
        onClick={header.column.getToggleSortingHandler()}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {flexRender(name, header.getContext())}
        {renderSortIcon(sortDirection)}
      </button>
    );
  } else {
    headerContent = (
      <div className="flex items-center">
        {flexRender(name, header.getContext())}
        {renderSortIcon(sortDirection)}
      </div>
    );
  }

  return (
    <ShadcnTableHead
      className={`${STYLING.COLORS.TEXT_LIGHT} font-normal text-md ${STYLING.CELL_PADDING.LEFT} ${
        STYLING.CELL_PADDING.RIGHT
      } 
        ${isFirstColumn ? 'rounded-tl-md' : ''} 
        ${isLastColumn ? 'rounded-tr-md' : ''}`}
      key={header.id}
    >
      {headerContent}
    </ShadcnTableHead>
  );
}

function TableHeader<TData>({ headerGroups }: ITableHeaderProps<TData>) {
  const { STYLING } = TABLE_CONSTANTS;
  const { createKeyDownHandler } = useTableSortHandlers<TData>();

  return (
    <ShadcnTableHeader className={`${STYLING.COLORS.HEADER_BG} rounded-md`}>
      {headerGroups.map((headerGroup) => (
        <ShadcnTableRow key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => {
            const canSort = header.column.getCanSort();
            const isFirstColumn = index === 0;
            const isLastColumn = index === headerGroup.headers.length - 1;
            const name = header.column.columnDef.header;
            const sortDirection = header.column.getIsSorted();

            return (
              <TableHeaderCell
                canSort={canSort}
                createKeyDownHandler={createKeyDownHandler}
                header={header}
                isFirstColumn={isFirstColumn}
                isLastColumn={isLastColumn}
                key={header.id}
                name={name}
                sortDirection={sortDirection}
              />
            );
          })}
        </ShadcnTableRow>
      ))}
    </ShadcnTableHeader>
  );
}

export { renderSortIcon, TableHeader, TableHeaderCell, useTableSortHandlers };
